import * as React from 'react';
import {BaseFieldProps, BaseFieldVariant} from '../../index';
import {useCallback, useMemo} from 'react';
import {useTheme} from '../../../../theme';
import {useIsDesktop} from '../../../../hooks/useIsDesktop';
import {createStyles, makeStyles, MuiThemeProvider, withStyles} from '@material-ui/core/styles';
import {Theme} from '../../../../theme/interface';
import {FormControl, FormHelperText, InputBase, InputLabel, MenuItem, Select} from '@material-ui/core';
import {Text} from '../../../text';
import {useStyles} from '../../../../hooks/styles';
import {DeviceSize} from '../../../../theme/helpers';
import {images} from '../../../../../assets/icons';
import TextField from '@material-ui/core/TextField';
import {Autocomplete} from '@material-ui/lab';
import {DictionaryItem} from '../../../../dictionaries/model';

const useMenuStyles = makeStyles(() => ({
  paper: {
    border: (props: Theme) => `1px solid ${props.input.common.tintColor}`,
    borderTopColor: (props: Theme) => `${props.input.common.tintColor}81`,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    marginLeft: -1,
    backgroundColor: (props: Theme) => props.color.primaryNonGreyBackground,
  },
}));

const useAutocompleteStyles = makeStyles(() => ({
  option: {
    padding: 0,

    '& .MuiMenuItem-root': {
      flexGrow: 1,
    },
  },
}));

export const BaseFieldSelect: React.FC<BaseFieldProps> = (props) => {
  const {
    variant,
    readOnly,
    label,
    values,
    input,
    meta,
    hint,
    isSearchEnabled,
    areOptionsLoading,
    shouldFilterOptions = true,
  } = props;
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const error: boolean = !!(meta?.touched && !!meta.error);

  const styles = useStyles(({theme: theme_, mediaOnly}) => {
    return {
      labelC: {
        ...mediaOnly(DeviceSize.desktop, theme_.fonts.h6Light, {...theme_.fonts.bodySmallLight}),
        color: theme_.color.labelText,
      },
    };
  });

  const menuStyles = useMenuStyles(theme);

  const autocompleteStyles = useAutocompleteStyles(theme);

  const labelComponent = useMemo(
    () => (
      <Text lineBreakMode='tail' numberOfLines={1} style={styles.labelC}>
        {label}
      </Text>
    ),
    [label, styles],
  );

  const handleChange = useCallback((e: React.ChangeEvent<{name?: string; value: unknown}>) => {
    input.onChange && input.onChange(e.target.value || null);
  }, []);

  const handleAutocompleteInputChange = useCallback((_: React.ChangeEvent<{}>, value: string) => {
    input.onChange && input.onChange(value);
  }, []);

  const handleAutocompleteChange = useCallback((_: React.ChangeEvent<{}>, value: string | DictionaryItem | null) => {
    input.onChange && input.onChange(typeof value === 'string' ? value : value?.value || '');
  }, []);

  const MenuItemComponent = useMemo(
    () => withStyles(createMenuItemStyles(theme))(MenuItem) as typeof MenuItem,
    [theme],
  );

  const StyledInputBase = useMemo(
    () => withStyles(createCustomStyles(theme, variant || 'standard', isDesktop, readOnly))(InputBase),
    [variant, isDesktop, readOnly, theme],
  );

  const StyledFormControl = useMemo(
    () =>
      withStyles(createCustomStyles(theme, variant || 'standard', isDesktop, readOnly, isSearchEnabled))(FormControl),
    [variant, isDesktop, readOnly, isSearchEnabled],
  );

  return (
    <MuiThemeProvider theme={theme.muiDefaultTheme}>
      <StyledFormControl fullWidth variant={variant} error={error}>
        {!isSearchEnabled ? (
          <>
            <InputLabel id='select-theme-label'>{labelComponent}</InputLabel>
            <Select
              name={input.name}
              variant={variant}
              readOnly={readOnly}
              value={input.value}
              label={labelComponent}
              onChange={handleChange}
              MenuProps={{
                PaperProps: {
                  className: menuStyles.paper,
                },
                variant: 'selectedMenu',
                disableAutoFocus: true,
                disableAutoFocusItem: true,
                autoFocus: false,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                getContentAnchorEl: null,
              }}
              IconComponent={images.ChevronUpDropdown}
              input={<StyledInputBase />}
              inputProps={{'data-testid': input.name}}>
              {(values || []).map((v) => (
                <MenuItemComponent key={v.id} value={v.value}>
                  {v.label ?? v.value}
                </MenuItemComponent>
              ))}
            </Select>
          </>
        ) : (
          <Autocomplete
            options={values ?? []}
            freeSolo
            fullWidth
            inputValue={input.value}
            onInputChange={handleAutocompleteInputChange}
            autoHighlight
            classes={{
              option: autocompleteStyles.option,
            }}
            loading={areOptionsLoading}
            loadingText='Загрузка...'
            noOptionsText='Ничего не найдено'
            value={values?.find((item) => item.value === input.value)}
            onChange={handleAutocompleteChange}
            getOptionLabel={(option) => option.label ?? option.value ?? ''}
            renderOption={(option) => (
              <MenuItemComponent value={option.value}>{option.label ?? option.value}</MenuItemComponent>
            )}
            filterOptions={!shouldFilterOptions ? (options) => options : undefined}
            disabled={readOnly}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                variant={variant}
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'off',
                }}
              />
            )}
          />
        )}
        {error || hint ? <FormHelperText>{error ? meta?.error.error : hint}</FormHelperText> : null}
      </StyledFormControl>
    </MuiThemeProvider>
  );
};

const createMenuItemStyles = (theme: Theme) => () =>
  createStyles({
    root: {
      '&.MuiMenuItem-root': {
        paddingTop: 12,
        paddingBottom: 12,
        paddingLeft: 16,
        paddingRight: 16,
        color: theme.color.textColor,
      },
      '&.Mui-selected, &:hover, &.Mui-selected:hover': {
        backgroundColor: theme.input.common.tintColor,
        color: theme.color.whiteTextColor,
      },
    },
  });

const createCustomStyles =
  (theme: Theme, variant: BaseFieldVariant, isDesktop: boolean, readOnly = false, isSearchEnabled = false) =>
  () => {
    if (variant === 'outlined') {
      return createStyles({
        root: {
          '&.MuiInputBase-root': {
            borderRadius: 4,
            border: '1px solid',
            borderColor: theme.input.variants.outlined.borderColor,
            outline: 'none',
            lineHeight: '28px',
            fontSize: theme.fonts.h5Light.fontSize,
            '&.Mui-focused': {
              borderColor: theme.input.common.tintColor,
              borderBottomColor: `${theme.input.common.tintColor}81`,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              '& .MuiSelect-icon': {
                color: theme.input.common.tintColor,
              },
            },
            '&:focus': {
              border: '1px solid',
              borderColor: theme.input.common.tintColor,
            },
            '& .MuiSelect-icon': {
              color: theme.color.nonActiveCheckBoxColor,
            },
            '&.Mui-error': {
              borderColor: theme.input.common.errorColor,
            },
          },
          '& .MuiSelect-root': {
            padding: '8px 16px',
          },
          '& .MuiInputLabel-outlined': {
            paddingRight: 8,
          },
          ...(!isSearchEnabled && {
            '& .MuiFormLabel-filled': {
              display: 'none',
            },
            '& .MuiInputLabel-outlined:not(.Mui-focused):not(.MuiFormLabel-filled)': {
              transform: isDesktop ? 'translate(14px, 10px) scale(1)' : 'translate(14px, 7px) scale(1)',
            },
            '& .MuiInputLabel-outlined.Mui-focused': {
              transform: isDesktop ? 'translate(14px, 10px) scale(1)' : 'translate(14px, 7px) scale(1)',
            },
          }),
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.color.primaryColor,
          },
          '& .MuiFormLabel-root.Mui-focused div': {
            color: theme.color.primaryColor,
          },
          ...(readOnly
            ? {
                '&.Mui-disabled': {
                  color: theme.color.textColor,
                },
              }
            : {}),
        },
      });
    }

    return createStyles({
      root: {
        '&.MuiInputBase-root': {
          color: theme.color.textColor,
          lineHeight: '28px',
          padding: '8px 10px',
          fontSize: theme.fonts.h5Light.fontSize,
        },
        '& .MuiInput-underline:before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
        '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
          borderColor: theme.input.variants.outlined.borderColor,
        },
      },
    });
  };
